import React from "react";

import InfoIcon from "@mui/icons-material/Info";
import DownloadIcon from "@mui/icons-material/SaveAlt";
import EditIcon from "@mui/icons-material/CreateOutlined";
import ShopIcon from "@mui/icons-material/ShoppingCart";
import PrintIcon from "@mui/icons-material/Print";
import NotesIcon from "@mui/icons-material/Subject";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import ZoomResetIcon from "@mui/icons-material/YoutubeSearchedFor";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import PresentationMarkerIcon from "@mui/icons-material/StarBorder";
import OpenIcon from "@mui/icons-material/Launch";
import ListAltIcon from '@mui/icons-material/ListAlt';

import generateUUID from "uuid/v4";

import { pathOr, propOr, union, range } from "ramda";

import generateTeacherMaterialGetters from "~/libs/dls";

import { isAppleiPadDevice } from "~/utils/index";
import { getLicenses } from "~/utils/dlsmaterial";
import { UnsecuredJWT } from "jose";

const LABELS = {
  DOWNLOAD: "herunterladen",
  EDIT: "bearbeiten",
  ARBEITSBLATTEDITOR: "arbeitsblateditor"
};

const NAMES = {
  DOWNLOAD: "Herunterladen",
  EDIT: "Bearbeiten",
  PRINT_SINGLE: "Einzelseite drucken / als PDF speichern",
  PRINT_MULTIPLE: "Drucken",
  NOTE: "Notizen",
  INFO: "Info",
  ORDER: "Zum Onlineshop",
  ZOOM_OUT: "Verkleinern",
  ZOOM_RESET: "Einpassen",
  ZOOM_IN: "Vergrößern",
  PRESENTATION_MARKER_ADD: "Zur Präsentation hinzufügen",
  PRESENTATION_MARKER_REMOVE: "Aus Präsentation entfernen",
  OPEN: "Öffnen",
  OPEN_ARBEITSBLATTEDITOR: "im Arbeitsblatteditor öffnen"
};

const ICONS = {
  DOWNLOAD: <DownloadIcon />,
  EDIT: <EditIcon />,
  PRINT: <PrintIcon />,
  NOTE: <NotesIcon />,
  INFO: <InfoIcon />,
  ORDER: <ShopIcon />,
  ZOOM_OUT: <ZoomOutIcon />,
  ZOOM_RESET: <ZoomResetIcon />,
  ZOOM_IN: <ZoomInIcon />,
  PRESENTATION_MARKER: <PresentationMarkerIcon />,
  OPEN: <OpenIcon />,
  OPEN_ARBEITSBLATTEDITOR: <ListAltIcon />
};

const isNoteMarked = note => note && note.text && note.text.length > 0;

const isPresentationActive = (presentationMarker, activePages, category) => {
  if (!presentationMarker || !presentationMarker.pages) return false;
  if (category.type === "page") {
    return presentationMarker.pages.some(page => activePages.includes(page));
  }
  if (category.type === "book") {
    return presentationMarker.book;
  }
  return false;
};

const getOrderUrl = (material, hasMissingUserLicense) => {
  let { orderNr } = material;
  if (hasMissingUserLicense) {
    const { partOfLicenses } = getLicenses(material);
    orderNr = partOfLicenses[0];
  }
  return `https://www.mildenberger-verlag.de/page.php?modul=GoShopping&op=do_ext_search&search_term=${orderNr}`;
};

const getFilelinks = material => propOr([], "filelinks", material).filter(link => link.label && link.path);

const getDownloadAction = (material, hasMissingUserLicense) => {
  if (hasMissingUserLicense) return null;
  const link = getFilelinks(material).find(link => link.label === LABELS.DOWNLOAD);
  if (!link) return null;
  return {
    icon: ICONS.DOWNLOAD,
    name: NAMES.DOWNLOAD,
    handler: event => {
      event.stopPropagation();

      const url = isAppleiPadDevice()
        ? generateTeacherMaterialGetters(material).getStaticFilePath(link.path)
        : generateTeacherMaterialGetters(material).getDownloadFilePath(link.path);
      const target = isAppleiPadDevice() ? "_blank" : "_self";
      window.open(url, target);
    }
  };
};

const getEditAction = (material, hasMissingUserLicense) => {
  if (hasMissingUserLicense) return null;
  const link = getFilelinks(material).find(link => link.label === LABELS.EDIT);
  if (!link) return null;
    
  return {
    icon: ICONS.EDIT,
    name: NAMES.EDIT,
    handler: event => {
      event.stopPropagation();
      window.open(generateTeacherMaterialGetters(material).getDownloadFilePath(link.path), "_self");
    }
  };
};

const getArbeitsblatteditorAction = (material, hasMissingUserLicense) => {
  if (hasMissingUserLicense) return null;
  const link = getFilelinks(material).find(link => link.label === LABELS.ARBEITSBLATTEDITOR);
  if (!link || !link.path.endsWith('arbeitsblatteditor_json')) return null;
  
  const abLink = generateTeacherMaterialGetters(material).getDownloadFilePath(link.path);
  return {
    icon: ICONS.OPEN_ARBEITSBLATTEDITOR,
    name: NAMES.OPEN_ARBEITSBLATTEDITOR,
    handler: event => {
      event.stopPropagation();
      const baseUrl = window.getClientConfiguration("", ["arbeitsblatteditor", "baseurl"]);
      const payload = { url: abLink.replace("buch.local", "devbuch.mildenberger-digital.de") };
      const jwt = new UnsecuredJWT(payload).setIssuedAt().encode();

      const link = `${baseUrl}?d=${jwt}`;
      window.open(link, "_blank");
    }
  };

};


export const getOpenAction = func => {
  if (!func) return null;
  return {
    icon: ICONS.OPEN,
    name: NAMES.OPEN,
    handler: event => {
      event.stopPropagation();
      func(event);
    },
    closeQuickdialOnAction: true
  };
};

// currentPage gets used for every viewer except of the main
const getPrintAction = (isAbleToPrint, material, printOrigin, showModal, currentPage, hasMissingUserLicense) => {
  if (hasMissingUserLicense) return null;
  if (printOrigin) {
    if (printOrigin === "main" && !isAbleToPrint) return null;
    if (printOrigin === "teacher" || printOrigin === "modal") {
      // material is only required for print jobs started from the dls list view
      if (!material || !material.printablePdf) return null;
    }
  }

  return {
    icon: ICONS.PRINT,
    name: printOrigin === "teacher" ? NAMES.PRINT_MULTIPLE : NAMES.PRINT_SINGLE,
    handler: event => {
      event.stopPropagation();

      if (printOrigin === "main") {
        showModal("PRINT_OPTIONS", { draggable: true });
      } else if (printOrigin === "modal" || printOrigin === "embedded") {
        const DLS = generateTeacherMaterialGetters(material);
        window.issueGlobalPrint(
          [DLS.getImagePath(currentPage, 2.0)],
          printOrigin,
          -1,
          `${material.title || material.name} - S.${currentPage}`,
          material.size
        );
      } else if (printOrigin === "teacher") {
        const DLS = generateTeacherMaterialGetters(material);
        const link = getFilelinks(material).find(link => link.label === LABELS.DOWNLOAD);

        // on iPad we open pdf directly instead
        if (isAppleiPadDevice() && link) {
          const url = DLS.getStaticFilePath(link.path);
          window.open(url, "_blank");
        } else {
          const pages = range(1, material.pagesCount + 1);
          window.issueGlobalPrint(
            pages.map(page => DLS.getImagePath(page, 2.0)),
            printOrigin,
            -1,
            `${material.title || material.name}`,
            material.size
          );
        }
      } else {
        console.warn(`Unknown printOrigin [${printOrigin}]`);
      }
    }
  };
};

export const getPresentationMarkerAction = (
  material,
  activePages,
  addPresentationMarker,
  updatePresentationMarker,
  category,
  isPresentationMode,
  hasMissingUserLicense
) => {
  if (hasMissingUserLicense) return null;
  const showPresentationMode = pathOr(
    true,
    ["book", "ui", "dls", "showPresentationMode"],
    window.getClientConfiguration()
  );

  if (isPresentationMode) return null;
  if (!material || !material.availableForPresentationmode) return null;
  if (!material.hasOwnProperty("presentationMarker")) return null;
  if (!addPresentationMarker || !updatePresentationMarker) return null;
  if (!showPresentationMode) {
    return null;
  }

  const presentationActive = isPresentationActive(material.presentationMarker, activePages, category);

  return {
    icon: ICONS.PRESENTATION_MARKER,
    name: presentationActive ? NAMES.PRESENTATION_MARKER_REMOVE : NAMES.PRESENTATION_MARKER_ADD,
    markedPresentation: presentationActive,
    handler: event => {
      event.stopPropagation();

      const { presentationMarker } = material;

      const getMarkedPages = () => {
        if (category.type !== "page") return presentationMarker.pages || [];
        return presentationActive
          ? presentationMarker.pages.filter(page => !activePages.includes(page))
          : union(presentationMarker.pages, activePages);
      };

      const getMarkedBook = () => {
        if (category.type !== "book") {
          return presentationMarker ? !!presentationMarker.book : false;
        }
        return !presentationActive;
      };

      if (presentationMarker) {
        updatePresentationMarker({
          ...presentationMarker,
          pages: getMarkedPages(),
          book: getMarkedBook()
        });
      } else {
        addPresentationMarker({
          _id: generateUUID(),
          type: "presentationMarker",
          materialId: material._id,
          book: getMarkedBook(),
          pages: category.type === "page" ? activePages : []
        });
      }
    }
  };
};

export const getPageNoteAction = (noteId, showModal, dlsOnly, isDlsMode, pagesHaveNotes) => {
  if (!noteId) return null;
  if (!showModal) return null;
  if (!isDlsMode && dlsOnly) return null;

  return {
    icon: ICONS.NOTE,
    name: NAMES.NOTE,
    markedNote: pagesHaveNotes,
    handler: event => {
      event.stopPropagation();
      showModal("NOTES", {
        noteId
      });
    }
  };
};

export const getMaterialNoteAction = (material, showModal, openNotesDialog) => {
  if (!material) return null;
  if (!showModal && !openNotesDialog) return null;

  return {
    icon: ICONS.NOTE,
    name: NAMES.NOTE,
    markedNote: isNoteMarked(material.note),
    handler: event => {
      event.stopPropagation();
      if (openNotesDialog) {
        openNotesDialog();
      } else {
        showModal("NOTES", {
          noteId: material.note ? material.note._id : null,
          material
        });
      }
    }
  };
};

const getInfoAction = (material, showModal, hasMissingUserLicense) => {
  if (hasMissingUserLicense) return null;
  if (!material || !material.description || !showModal) return null;
  return {
    icon: ICONS.INFO,
    name: NAMES.INFO,
    handler: event => {
      event.stopPropagation();
      showModal("MATERIAL_INFO", {
        title: material.title || material.name,
        description: material.description
      });
    }
  };
};

const getOrderAction = (material, hasMissingUserLicense) => {
  if (!material) return null;
  if (!material.orderNr && !hasMissingUserLicense) return null;
  return {
    icon: ICONS.ORDER,
    name: NAMES.ORDER,
    handler: event => {
      event.stopPropagation();
      window.open(getOrderUrl(material, hasMissingUserLicense), "_blank");
    }
  };
};

const getZoomActions = (zoomController, hasMissingUserLicense) => {
  if (!zoomController || hasMissingUserLicense) return [];
  return [
    {
      icon: ICONS.ZOOM_OUT,
      name: NAMES.ZOOM_OUT,
      handler: () => zoomController.zoomOut()
    },
    {
      icon: ICONS.ZOOM_RESET,
      name: NAMES.ZOOM_RESET,
      handler: () => zoomController.zoomToFitAgain()
    },
    {
      icon: ICONS.ZOOM_IN,
      name: NAMES.ZOOM_IN,
      handler: () => zoomController.zoomIn()
    }
  ];
};

export const getQuickDialActions = ({
  material,
  zoomController,
  showModal,
  activePages,
  addPresentationMarker,
  updatePresentationMarker,
  category,
  printOrigin,
  isPresentationMode,
  openNotesDialog,
  isAbleToPrint,
  currentPage,
  hasMissingUserLicense,
  openFunc,
  noteId,
  dlsOnly,
  isDlsMode,
  pagesHaveNotes
}) =>
  [
    getOpenAction(openFunc),
    getPrintAction(isAbleToPrint, material, printOrigin, showModal, currentPage, hasMissingUserLicense),
    getDownloadAction(material, hasMissingUserLicense),
    getEditAction(material, hasMissingUserLicense),
    getArbeitsblatteditorAction(material, hasMissingUserLicense),
    getInfoAction(material, showModal, hasMissingUserLicense),
    getMaterialNoteAction(material, showModal, openNotesDialog),
    getPresentationMarkerAction(
      material,
      activePages,
      addPresentationMarker,
      updatePresentationMarker,
      category,
      isPresentationMode,
      hasMissingUserLicense
    ),
    ...getZoomActions(zoomController, hasMissingUserLicense),
    getPageNoteAction(noteId, showModal, dlsOnly, isDlsMode, pagesHaveNotes),
    getOrderAction(material, hasMissingUserLicense),
    // getLinkAction(material, showModal)
  ].filter(action => action !== null);
